// client/src/components/AdminPage.js
import React, { useState, useCallback, useEffect } from 'react';
import {
    Box, Container, Typography, Button, Dialog, DialogActions, DialogContent,
    DialogContentText, DialogTitle, Snackbar, Alert, Paper, AppBar, Toolbar,
    IconButton, useMediaQuery, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper as MuiPaper, CircularProgress
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LoadingButton } from '@mui/lab';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useNavigate } from 'react-router-dom';

import { resetAllRatingsAPI, exportIsbnsCsvAPI, fetchActivityLogAPI, fetchCurrentLoansAPI } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useLocations } from '../hooks/useLocations';

// Kleine Hilfskomponente für den Bestätigungsdialog
const ConfirmDialog = ({ open, onClose, onConfirm, title, contentText, confirmText = "Bestätigen", cancelText = "Abbrechen" }) => (
    <Dialog open={open} onClose={onClose}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
            <WarningAmberIcon color="warning" sx={{ mr: 1 }} />
            {title}
        </DialogTitle>
        <DialogContent>
            <DialogContentText>{contentText}</DialogContentText>
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose}>{cancelText}</Button>
            <Button onClick={onConfirm} color="error" autoFocus>
                {confirmText}
            </Button>
        </DialogActions>
    </Dialog>
);

function AdminPage() {
    const [isResetting, setIsResetting] = useState(false);
    const [resetError, setResetError] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

    const { handleApiError } = useAuth();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const showSnackbar = useCallback((message, severity = 'info') => {
        setSnackbar({ open: true, message, severity });
    }, []);

    const handleSnackbarClose = useCallback((event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbar(prev => ({ ...prev, open: false }));
    }, []);

    const handleOpenConfirm = () => setConfirmOpen(true);
    const handleCloseConfirm = () => setConfirmOpen(false);

    const handleResetRatings = useCallback(async () => {
        handleCloseConfirm(); // Dialog sofort schließen
        setIsResetting(true);
        setResetError(null);
        try {
            const result = await resetAllRatingsAPI();
            showSnackbar(result.message || 'Alle Bewertungen erfolgreich zurückgesetzt.', 'success');
        } catch (err) {
            handleApiError(err);
            setResetError(err.message);
            showSnackbar(`Fehler beim Zurücksetzen: ${err.message}`, 'error');
        } finally {
            setIsResetting(false);
        }
    }, [handleApiError, showSnackbar]);

    // --- CSV Export State ---
    const { locations, fetchLocations, isLoading: isLocationsLoading } = useLocations();
    const [selectedLocation, setSelectedLocation] = useState('');
    const [isExporting, setIsExporting] = useState(false);

    // Lade die Lagerorte beim ersten Rendern
    useEffect(() => {
        fetchLocations();
    }, [fetchLocations]);

    // CSV Export Handler
    const handleExportCsv = useCallback(async () => {
        if (!selectedLocation) {
            showSnackbar('Bitte wähle einen Lagerort für den Export.', 'warning');
            return;
        }
        setIsExporting(true);
        try {
            const blob = await exportIsbnsCsvAPI(selectedLocation);
            // Download auslösen
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'isbns_export.csv';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            showSnackbar('CSV-Export erfolgreich!', 'success');
        } catch (err) {
            showSnackbar(`Fehler beim Export: ${err.message}`, 'error');
        } finally {
            setIsExporting(false);
        }
    }, [selectedLocation, showSnackbar]);

    // --- Aktivitätslog State ---
    const [activityLog, setActivityLog] = useState([]);
    const [isLogLoading, setIsLogLoading] = useState(false);
    const [logError, setLogError] = useState(null);

    const loadActivityLog = useCallback(async () => {
        setIsLogLoading(true);
        setLogError(null);
        try {
            const logs = await fetchActivityLogAPI({ limit: 50 });
            setActivityLog(logs);
        } catch (err) {
            setLogError(err.message);
        } finally {
            setIsLogLoading(false);
        }
    }, []);

    useEffect(() => { loadActivityLog(); }, [loadActivityLog]);

    // --- Übersicht verliehene Bücher ---
    const [loans, setLoans] = useState([]);
    const [isLoansLoading, setIsLoansLoading] = useState(false);
    const [loansError, setLoansError] = useState(null);
    const loadLoans = useCallback(async () => {
        setIsLoansLoading(true);
        setLoansError(null);
        try {
            const data = await fetchCurrentLoansAPI();
            setLoans(data);
        } catch (err) {
            setLoansError(err.message);
        } finally {
            setIsLoansLoading(false);
        }
    }, []);
    useEffect(() => { loadLoans(); }, [loadLoans]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
            {/* AppBar */}
            <AppBar position="static">
                <Toolbar>
                    <IconButton edge="start" color="inherit" onClick={() => navigate('/booklist')} aria-label="Zurück">
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                        Admin Bereich
                    </Typography>
                    {/* Ggf. weitere Actions hier */}
                </Toolbar>
            </AppBar>

            {/* Hauptinhalt */}
            <Container component="main" sx={{ flexGrow: 1, py: { xs: 2, sm: 3 } }}>
                <Typography variant={isMobile ? "h5" : "h4"} component="h1" gutterBottom>
                    Admin Funktionen
                </Typography>

                {resetError && <Alert severity="error" sx={{ mb: 2 }}>{resetError}</Alert>}

                <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 } }}>
                    <Typography variant="h6" gutterBottom>Bewertungsdaten</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Setzt die Status "Gelesen"/"Will ich lesen" und "Behalten"/"Kann weg" für **alle** deine bewerteten Bücher zurück.
                        Diese Aktion kann nicht rückgängig gemacht werden.
                    </Typography>
                    <LoadingButton
                        variant="contained"
                        color="error"
                        onClick={handleOpenConfirm}
                        loading={isResetting}
                        loadingPosition="start"
                        startIcon={<DeleteForeverIcon />}
                        disabled={isResetting}
                    >
                        Alle meine Bewertungen zurücksetzen
                    </LoadingButton>
                </Paper>

                <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mt: 3 }}>
                    <Typography variant="h6" gutterBottom>CSV-Export der ISBNs nach Lagerort</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Exportiere eine CSV-Liste aller ISBNs (und Titel) für einen ausgewählten Lagerort.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <select
                            value={selectedLocation}
                            onChange={e => setSelectedLocation(e.target.value)}
                            disabled={isLocationsLoading || isExporting}
                            style={{ minWidth: 200, padding: 8, borderRadius: 4 }}
                        >
                            <option value="">Lagerort wählen...</option>
                            {locations.map(loc => (
                                <option key={loc._id} value={loc._id}>
                                    {loc.name} {loc.adresse ? `- ${loc.adresse}` : ''}
                                </option>
                            ))}
                        </select>
                        <LoadingButton
                            variant="contained"
                            color="primary"
                            onClick={handleExportCsv}
                            loading={isExporting}
                            disabled={!selectedLocation || isExporting}
                        >
                            Exportieren
                        </LoadingButton>
                    </Box>
                </Paper>

                {/* Aktivitätslog */}
                <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mt: 3 }}>
                    <Typography variant="h6" gutterBottom>Aktivitätslog (letzte 50 Aktionen)</Typography>
                    {isLogLoading ? (
                        <CircularProgress size={24} sx={{ m: 2 }} />
                    ) : logError ? (
                        <Alert severity="error">{logError}</Alert>
                    ) : (
                        <TableContainer component={MuiPaper} sx={{ maxHeight: 350 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Zeitpunkt</TableCell>
                                        <TableCell>Aktion</TableCell>
                                        <TableCell>Buch</TableCell>
                                        <TableCell>User</TableCell>
                                        <TableCell>Details</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {activityLog.map(log => (
                                        <TableRow key={log._id}>
                                            <TableCell>{new Date(log.createdAt).toLocaleString()}</TableCell>
                                            <TableCell>{log.action}</TableCell>
                                            <TableCell>{log.book?.title || log.book?._id || '-'}</TableCell>
                                            <TableCell>{log.user?.email || log.user?._id || '-'}</TableCell>
                                            <TableCell>
                                                {log.details ? <pre style={{ margin: 0, fontSize: 11 }}>{JSON.stringify(log.details, null, 2)}</pre> : '-'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Paper>

                {/* Übersicht verliehene Bücher */}
                <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 }, mt: 3 }}>
                    <Typography variant="h6" gutterBottom>Verliehene Bücher</Typography>
                    {isLoansLoading ? (
                        <CircularProgress size={24} sx={{ m: 2 }} />
                    ) : loansError ? (
                        <Alert severity="error">{loansError}</Alert>
                    ) : (
                        <TableContainer component={MuiPaper} sx={{ maxHeight: 350 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Buch</TableCell>
                                        <TableCell>Leihender</TableCell>
                                        <TableCell>Ausgeliehen seit</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {loans.map(loan => (
                                        <TableRow key={loan._id}>
                                            <TableCell>{loan.book?.title || loan.book?._id || '-'}</TableCell>
                                            <TableCell>{loan.user?.email || loan.user?._id || '-'}</TableCell>
                                            <TableCell>{loan.loanedAt ? new Date(loan.loanedAt).toLocaleDateString() : '-'}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Paper>

                {/* Weitere Admin-Funktionen können hier als weitere Paper-Elemente hinzugefügt werden */}

            </Container>

            {/* Bestätigungsdialog */}
            <ConfirmDialog
                open={confirmOpen}
                onClose={handleCloseConfirm}
                onConfirm={handleResetRatings}
                title="Wirklich alle Bewertungen zurücksetzen?"
                contentText="Bist du sicher, dass du alle deine Lesestatus ('Gelesen'/'Will ich lesen') und Behalten-Status ('Behalten'/'Kann weg') für alle Bücher unwiderruflich löschen möchtest?"
                confirmText="Ja, alle zurücksetzen"
                cancelText="Abbrechen"
            />

            {/* Snackbar */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default AdminPage;
